"use client";

import { useState, type FormEvent } from "react";
import { LoaderCircle } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useCreateComment } from "@/hooks/use-feed";
import { getErrorMessage } from "@/lib/utils";

type CommentFormProps = {
  postId: string;
  currentUserId: string | null;
  parentCommentId?: string | null;
  placeholder?: string;
  onSuccess?: () => void;
  compact?: boolean;
};

export function CommentForm({
  postId,
  currentUserId,
  parentCommentId = null,
  placeholder = "Írj hozzászólást...",
  onSuccess,
  compact = false,
}: CommentFormProps) {
  const createComment = useCreateComment(postId);
  const [content, setContent] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (!currentUserId) {
      setError("Hozzászóláshoz be kell jelentkezned.");
      return;
    }

    const trimmed = content.trim();
    if (!trimmed) {
      setError("A hozzászólás nem lehet üres.");
      return;
    }

    setError(null);

    try {
      await createComment.mutateAsync({
        userId: currentUserId,
        content: trimmed,
        parentCommentId,
      });
      setContent("");
      onSuccess?.();
    } catch (submitError) {
      setError(getErrorMessage(submitError));
    }
  }

  if (!currentUserId) {
    return (
      <p className="text-sm text-muted-foreground">
        Hozzászóláshoz jelentkezz be.
      </p>
    );
  }

  return (
    <form className="space-y-2" onSubmit={handleSubmit}>
      <Textarea
        value={content}
        onChange={(event) => setContent(event.target.value.slice(0, 10000))}
        placeholder={placeholder}
        rows={compact ? 2 : 3}
      />
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={createComment.isPending}>
          {createComment.isPending ? (
            <LoaderCircle className="size-4 animate-spin" />
          ) : null}
          {parentCommentId ? "Válasz küldése" : "Hozzászólás"}
        </Button>
      </div>
    </form>
  );
}
